import { useCallback, useEffect } from 'react';
import { bugtracker } from '@99designs/design-services-common';
import { WorkEntitiesInput } from '@99designs/graph-utils/types';
import useDebouncedState from '../../useDebouncedState';
import {
    WorkEntitiesQuery,
    useWorkEntitiesLazyQuery,
    useWorkEntitiesQuery,
} from './referenceProjects.generated';

const DEFAULT_PAGE_SIZE = 5;
const TENANTS = 'VISTAPRINT-PROD';
const DEBOUNCE_DELAY = 300;
const DEBOUNCE_MAX_WAIT = 10000;

function parseWorkEntitiesResponse(data?: WorkEntitiesQuery) {
    const totalPages = data?.workEntities?.pagination.totalPages;
    const totalResults = data?.workEntities?.pagination.totalResults;
    const workEntities = data?.workEntities?.results;

    return {
        totalPages,
        totalResults,
        workEntities,
    };
}

export function useWorkEntities(currentPage: number, searchTerm: string) {
    const {
        loading: initialLoading,
        error: initialWorkEntitiesError,
        data: initialData,
    } = useWorkEntitiesQuery({
        variables: {
            input: {
                pageNumber: currentPage,
                pageSize: DEFAULT_PAGE_SIZE,
                searchTerm: '',
                tenants: TENANTS,
            } as WorkEntitiesInput,
        },
    });

    const {
        workEntities: pageWorkEntities,
        totalPages: allTotalPages,
        totalResults: allTotalResults,
    } = parseWorkEntitiesResponse(initialData);

    const [searchWorkEntities, { loading: searchLoading, error: searchError, data: searchData }] =
        useWorkEntitiesLazyQuery();

    const search = useCallback(
        (searchTerm: string, currentPage: number) => {
            return searchWorkEntities({
                variables: {
                    input: {
                        pageNumber: currentPage,
                        pageSize: DEFAULT_PAGE_SIZE,
                        searchTerm,
                        tenants: TENANTS,
                    } as WorkEntitiesInput,
                },
            });
        },
        [searchWorkEntities]
    );

    const {
        workEntities: searchPageWorkEntities,
        totalPages: searchTotalPages,
        totalResults: searchTotalResults,
    } = parseWorkEntitiesResponse(searchData);

    const pastWorkEntities = (searchPageWorkEntities ?? pageWorkEntities) || [];
    const totalPages = (searchTotalPages ?? allTotalPages) || 0;
    const totalResults = (searchTotalResults ?? allTotalResults) || 0;

    const searchWithDebounce = useDebouncedState(search, DEBOUNCE_DELAY, DEBOUNCE_MAX_WAIT);

    const error = initialWorkEntitiesError || searchError;

    useEffect(() => {
        // Fetch initial data
        searchWithDebounce(searchTerm, currentPage);
    }, [searchTerm, searchWithDebounce]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (error) {
            bugtracker.notify(error, (event) => {
                event.addMetadata('useWorkEntities', {
                    searchTerm,
                    currentPage,
                });
            });
            return;
        }
    }, [searchTerm, error, currentPage]);

    return {
        error,
        loading: initialLoading || searchLoading,
        pastWorkEntities,
        totalPages,
        allTotalResults,
        totalResults,
        search,
        searchWithDebounce,
    };
}
